import * as React from 'react';
import styled from 'styled-components';
import { ICollapsibleItemProps } from './collapsible-types';
import { CollapsibleContext } from './collapsible-context';
import { CollapsibleItemArrow, CollapsibleItemHeader, HeaderWrapper, CollapsibleItemContent } from './parts';
import { isFunction } from '@agilepoint/utils-common';
import { useKeyStrokeListener, useChooseRef } from '../../utils';

interface IItemProps extends Required<ICollapsibleItemProps> {
    canGetItemInView: () => boolean;
    getItemInView: (itemRef: React.RefObject<HTMLDivElement>, isExpanded: boolean) => void;
}

const CollapsibleItemWrapper = styled.div.attrs({
    className: String('ap-collapsible-itemWrapper')
})`
    border-top-width:  ${({ theme: { collapsible: { composites: { item } } } }) => item.borderWidth};
    border-top-style: solid;
    border-top-color: ${({ theme: { collapsible: { colors } } }) => colors.item.borderColor};
    &:hover {
        border-top: none;
        border-radius: ${({ theme: { collapsible: { composites } } }) => composites.item.borderRadius};
        box-shadow: ${({ theme: { collapsible: { composites } } }) => composites.item.boxShadow};
    }
    &:hover + .ap-collapsible-itemWrapper {
        border-top: none;
    }
`;

export function CollapsibleItem(props: ICollapsibleItemProps) {
    const { id } = props;
    const { upsert, remove } = React.useContext(CollapsibleContext);

    React.useMemo(() => upsert({ ...props }), [props, upsert]);

    React.useEffect(() => () => remove(id), [remove, id]);

    return null;
}

export function getItemClassName({ isExpanded, className }: Pick<IItemProps, 'isExpanded' | 'className'>) {
    const itemClassName = isExpanded ? 'ap-expanded' : 'ap-collapsed';
    return className ? `${className} ${itemClassName}` : itemClassName;
}

export const Item = React.memo((props: IItemProps) => {
    const {
        isExpanded, children, maxContentHeight,
        onChange, id, canGetItemInView, arrowPosition,
        disabled, header, onRenderHeader, getItemInView, elementRef, className
    } = props;

    const isItemClicked = React.useRef(false);
    const wrapperRef = React.useRef<HTMLDivElement>(null);
    const rootElementRef = useChooseRef(elementRef);
    const itemClassName = getItemClassName({ isExpanded, className });

    React.useEffect(() => {
        if (isItemClicked.current || canGetItemInView()) {
            getItemInView(rootElementRef, isExpanded);
        }
        isItemClicked.current = false;
    }, [canGetItemInView, getItemInView, isExpanded, rootElementRef]);

    const toggleExpandState = React.useCallback(() => {
        if (!isFunction(onChange) || disabled) {
            return;
        }
        onChange(id);
        isItemClicked.current = true;
    }, [onChange, id, disabled]);

    const { subscribe, unsubscribe } = useKeyStrokeListener(wrapperRef);

    React.useEffect(() => {
        subscribe(toggleExpandState, 'Enter');
        return unsubscribe;
    }, [subscribe, toggleExpandState, unsubscribe]);

    const renderHeader = React.useCallback(() => (onRenderHeader ? onRenderHeader({ isExpanded, header }) : header), [header, isExpanded, onRenderHeader]);

    const renderItemHeaderContent = React.useCallback(() => (
        <>
            <CollapsibleItemArrow isExpanded={isExpanded} />
            <CollapsibleItemHeader $arrowPosition={arrowPosition}>
                {renderHeader()}
            </CollapsibleItemHeader>
        </>
    ), [isExpanded, renderHeader, arrowPosition]);

    const renderItemHeaderWrapper = React.useCallback(() => (
        <HeaderWrapper
            ref={wrapperRef}
            tabIndex={0}
            onClick={toggleExpandState}
            alignItems="center"
            flexDirection={arrowPosition === 'start' ? 'row' : 'row-reverse'}
            justifyContent={arrowPosition === 'start' ? '' : 'space-between'}>
            {renderItemHeaderContent()}
        </HeaderWrapper>
    ), [renderItemHeaderContent, toggleExpandState, arrowPosition]);

    const renderItemContent = React.useCallback(() => (isExpanded ? (
        <CollapsibleItemContent isExpanded={isExpanded} maxContentHeight={maxContentHeight} $arrowPosition={arrowPosition}>
            {children}
        </CollapsibleItemContent>
    ) : null), [isExpanded, children, maxContentHeight, arrowPosition]);

    return (
        <CollapsibleItemWrapper
            ref={rootElementRef}
            className={itemClassName}
        >
            {renderItemHeaderWrapper()}
            {renderItemContent()}
        </CollapsibleItemWrapper>
    );
});

Item.displayName = 'CollapsibleItem';
